import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Observable, of, forkJoin } from 'rxjs';
import { map, tap, shareReplay } from 'rxjs/operators';
import { MaterialModule } from '../../../shared/material.module';
import { UserService } from '../../../services/user.service';
import { AppUserBase } from '../../../models/appUserBase';
import { LargeShareholderCategory } from '../../../models/largeShareholderCategory';
import { LutService } from '../../../services/lut.service';
import { PyramidalShareholderSearchFilter } from '../../../models/pyramidalShareholderSearchFilter';
import { TextNormalizeDirective } from '../../../directives/text-normalize.directive';

export interface PyramidalShareholderSearchFieldsConfig {
  showName?: boolean;
  showCategory?: boolean;
  showSubCategory?: boolean;
  showUser?: boolean;
}

@Component({
  selector: 'app-pyramidal-shareholder-search-box',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    FormsModule,
    TextNormalizeDirective
],
  templateUrl: './pyramidal-shareholder-search-box.component.html',
  styleUrl: './pyramidal-shareholder-search-box.component.scss'
})
export class PyramidalShareholderSearchBoxComponent implements OnInit, OnChanges {
  @Input() isSearchVisible: boolean = false;
  @Input() initialFilter: Partial<PyramidalShareholderSearchFilter> = {};
  @Input() fieldsConfig: PyramidalShareholderSearchFieldsConfig = {
    showName: true,
    showCategory: true,
    showSubCategory: true,
    showUser: true
  };

  @Output() pyramidalShareholderSearchFilter = new EventEmitter<Partial<PyramidalShareholderSearchFilter>>();
  @Output() showPyramidMembershipsChange = new EventEmitter<boolean>();

  showPyramidMemberships = false;

  categories$!: Observable<LargeShareholderCategory[] | null>;
  subCategories$: Observable<LargeShareholderCategory[]> = of([]);
  allCategories: LargeShareholderCategory[] = [];
  users$!: Observable<AppUserBase[]>;

  searchForm: Partial<PyramidalShareholderSearchFilter> = {
    name: undefined,
    categoryId: undefined,
    subCategoryId: undefined,
    createdBy: undefined
  };

  constructor(private lutService: LutService, private userService: UserService) {}

  ngOnInit(): void {
    // Load categories observable with shareReplay to avoid multiple HTTP requests
    const categoriesData$ = this.lutService.getLargeShareholderCategories().pipe(
      tap(categories => {
        this.allCategories = categories;
      }),
      shareReplay(1)
    );

    // Wait for categories to load before populating the search form
    categoriesData$.subscribe(() => {
      this.populateSearchForm();
    });

    // Set up the observables for the template
    this.categories$ = categoriesData$.pipe(
      map(categories => categories
        .filter(category => category.parentId === null)
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      )
    );

    // Load users
    this.users$ = this.userService.getUsers(1, 100, 'FullName', true).pipe(
      map(response => response.data)
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialFilter'] && !changes['initialFilter'].firstChange) {
      this.populateSearchForm();
    }
  }

  private populateSearchForm(): void {
    if (this.initialFilter && Object.keys(this.initialFilter).length > 0) {
      this.searchForm = {
        name: this.initialFilter.name,
        categoryId: this.initialFilter.categoryId,
        subCategoryId: this.initialFilter.subCategoryId,
        createdBy: this.initialFilter.createdBy
      };

      // Populate subcategories if categoryId is present
      if (this.initialFilter.categoryId) {
        this.subCategories$ = of(
          this.allCategories
            .filter(category => category.parentId === this.initialFilter.categoryId)
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
        );
      }
    }
  }

  onCategoryChange(): void {
    if (this.searchForm.categoryId) {
      this.subCategories$ = of(
        this.allCategories
          .filter(category => category.parentId === this.searchForm.categoryId)
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      );
    } else {
      this.subCategories$ = of([]);
      this.searchForm.subCategoryId = undefined;
    }
  }

  onSearch(): void {
    this.pyramidalShareholderSearchFilter.emit({ ...this.searchForm });
  }

  onShowPyramidMembershipsChange(): void {
    this.showPyramidMembershipsChange.emit(this.showPyramidMemberships);
  }

  clearSearch(): void {
    this.searchForm = {
      name: undefined,
      categoryId: undefined,
      subCategoryId: undefined,
      createdBy: undefined
    };
    this.subCategories$ = of([]);
    this.pyramidalShareholderSearchFilter.emit({ ...this.searchForm });
  }
}
